import { useQuery } from '@tanstack/react-query';
import { ApiError } from './apiClient';
import { useApiClient } from './useApiClient';
import { themeProfiles } from '@/theme/themeProfiles';

const VISUAL_EFFECTS_BASE = '/visual-effects';

export interface VisualPalette {
  id?: string;
  name?: string;
  description?: string;
  paletteId: string;
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  text: string;
  contrastRatio: {
    primaryOnBackground: number;
    textOnBackground: number;
  };
  metadata?: Record<string, unknown>;
  expiresAt?: string;
  fallbackPaletteId?: string;
}

interface PaletteQueryParams {
  lat?: number;
  lng?: number;
  locationId?: string;
  preferredTheme?: string;
  enabled?: boolean;
}

const fallbackPalette: VisualPalette = {
  id: 'default',
  name: 'Default',
  description: 'Built-in theme',
  paletteId: themeProfiles.normal.id,
  primary: themeProfiles.normal.tokens.primary,
  secondary: themeProfiles.normal.tokens.secondary,
  accent: themeProfiles.normal.tokens.accent,
  background: themeProfiles.normal.tokens.background,
  text: themeProfiles.normal.tokens.text,
  contrastRatio: {
    primaryOnBackground: 4.6,
    textOnBackground: 8.9,
  },
  metadata: {
    source: 'local-fallback',
  },
};

export const useVisualPalette = ({
  lat,
  lng,
  locationId,
  preferredTheme,
  enabled = true,
}: PaletteQueryParams) => {
  const client = useApiClient();

  return useQuery({
    queryKey: ['visual-effects', { lat, lng, locationId, preferredTheme }],
    enabled,
    queryFn: async () => {
      try {
        return await client.get<VisualPalette>(`${VISUAL_EFFECTS_BASE}/palette`, {
          searchParams: {
            lat,
            lng,
            locationId,
            preferredTheme,
          },
        });
      } catch (error) {
        if (error instanceof ApiError) {
          return fallbackPalette;
        }
        throw error;
      }
    },
  });
};
